import { create } from 'zustand';
import { FormState, FormResponse, FormSection } from '../types/form';
import { formSections } from '../config/formFields';
import { documentTypeConfig } from '../config/documentConfigs';
import { shouldSkipField, getVisibleFields } from '../utils/conditionalValidation';

// Calculate total steps for a specific path based on initial responses
const calculatePathSteps = (sections: { [key: string]: FormSection }, responses: FormResponse): number => {
  let totalSteps = 0;
  let currentSectionId = 'initial';
  
  while (currentSectionId) {
    const section = sections[currentSectionId];
    if (!section) break;
    
    totalSteps += section.fields.length;
    
    // Get the last field of the current section
    const lastField = section.fields[section.fields.length - 1];
    
    // Check if there's a next section based on responses
    if (lastField.nextStep) {
      const response = responses[lastField.id];
      const condition = lastField.nextStep.conditions?.find(c => c.value === response);
      
      if (condition) {
        currentSectionId = condition.goto;
      } else if (lastField.nextStep.default) {
        currentSectionId = lastField.nextStep.default;
      } else {
        break;
      }
    } else {
      break;
    }
  }
  
  return totalSteps;
};

export const useFormStore = create<FormState>((set, get) => ({
  currentStep: 0,
  currentSection: 'initial',
  sections: formSections,
  responses: {},
  totalSteps: formSections.initial.fields.length, // Start with initial section length
  currentStepOverall: 0,
  documentType: '',

  setDocumentType: (documentType) => {
    const config = documentTypeConfig[documentType];
    if (config) {
      const docSections = config.formSections;
      const firstSection = Object.keys(docSections)[0];

      // Calculate total steps across all sections
      const totalSteps = Object.values(docSections).reduce((sum, section) => {
        return sum + section.fields.length;
      }, 0);

      set({
        documentType,
        sections: docSections,
        currentSection: firstSection || 'details',
        currentStep: 0,
        currentStepOverall: 0,
        totalSteps,
        responses: {}
      });
    } else {
      set({
        documentType,
        sections: formSections,
        currentSection: 'initial',
        currentStep: 0,
        currentStepOverall: 0,
        totalSteps: formSections.initial?.fields.length || 0,
        responses: {}
      });
    }
  },
  
  setResponse: (fieldId, value) =>
    set((state) => {
      const newResponses = { ...state.responses, [fieldId]: value };

      // For document-specific flows (like standard-residential), don't recalculate total steps
      // Only recalculate for multi-path flows (when starting from 'initial' section)
      let newTotalSteps = state.totalSteps;
      if (state.currentSection === 'initial' || Object.keys(state.sections).includes('initial')) {
        newTotalSteps = calculatePathSteps(state.sections, newResponses);
      }

      return {
        responses: newResponses,
        totalSteps: newTotalSteps,
      };
    }),
    
  nextStep: () => {
    const state = get();
    const currentSection = state.sections[state.currentSection];
    const currentField = currentSection.fields[state.currentStep];

    // Find the next visible (non-skipped) field in the current section
    let nextStepIndex = state.currentStep + 1;
    while (nextStepIndex < currentSection.fields.length) {
      const nextField = currentSection.fields[nextStepIndex];
      if (!shouldSkipField(nextField, state.responses)) {
        set({
          currentStep: nextStepIndex,
          currentStepOverall: state.currentStepOverall + 1
        });
        return;
      }
      nextStepIndex++;
    }

    // If we're at the last visible field in the current section
    if (state.currentStep === currentSection.fields.length - 1) {
      // Check if there's a next step defined
      if (currentField.nextStep) {
        const response = state.responses[currentField.id];
        const condition = currentField.nextStep.conditions?.find(c => c.value === response);
        
        // If we have a matching condition, go to that section
        if (condition && state.sections[condition.goto]) {
          set({ 
            currentSection: condition.goto, 
            currentStep: 0,
            currentStepOverall: state.currentStepOverall + 1,
            totalSteps: calculatePathSteps(state.sections, state.responses)
          });
          return;
        }
        
        // If we have a default and it's valid, go to that section
        if (currentField.nextStep.default && state.sections[currentField.nextStep.default]) {
          set({ 
            currentSection: currentField.nextStep.default, 
            currentStep: 0,
            currentStepOverall: state.currentStepOverall + 1,
            totalSteps: calculatePathSteps(state.sections, state.responses)
          });
          return;
        }
      }
      // If no valid next section is found, stay on the current step
      return;
    }

    // No more visible fields found in current section, stay here
    return;
  },
  
  previousStep: () =>
    set((state) => {
      const currentSection = state.sections[state.currentSection];

      // If we're at the first step of a section that's not 'initial'
      if (state.currentStep === 0 && state.currentSection !== 'initial') {
        // Find the previous section
        const previousSectionEntry = Object.entries(state.sections).find(([_, section]) => {
          const lastField = section.fields[section.fields.length - 1];
          return lastField.nextStep?.conditions?.some(c => c.goto === state.currentSection) ||
                 lastField.nextStep?.default === state.currentSection;
        });

        if (previousSectionEntry) {
          const [previousSectionId, previousSection] = previousSectionEntry;
          const visibleFields = getVisibleFields(previousSection.fields, state.responses);
          const lastVisibleIndex = previousSection.fields.indexOf(visibleFields[visibleFields.length - 1]);

          return {
            currentSection: previousSectionId,
            currentStep: lastVisibleIndex >= 0 ? lastVisibleIndex : previousSection.fields.length - 1,
            currentStepOverall: state.currentStepOverall - 1,
            totalSteps: calculatePathSteps(state.sections, state.responses)
          };
        }
      }

      // Find the previous visible (non-skipped) field in the current section
      let prevStepIndex = state.currentStep - 1;
      while (prevStepIndex >= 0) {
        const prevField = currentSection.fields[prevStepIndex];
        if (!shouldSkipField(prevField, state.responses)) {
          return {
            currentStep: prevStepIndex,
            currentStepOverall: Math.max(state.currentStepOverall - 1, 0)
          };
        }
        prevStepIndex--;
      }

      // No previous visible field, stay at current position
      return {
        currentStep: state.currentStep,
        currentStepOverall: state.currentStepOverall
      };
    }),
    
  setSection: (sectionId) =>
    set((state) => ({
      currentSection: sectionId,
      currentStep: 0,
      totalSteps: calculatePathSteps(state.sections, state.responses)
    })),
    
  reset: () =>
    set({
      currentStep: 0,
      currentSection: 'initial',
      currentStepOverall: 0,
      responses: {},
      totalSteps: formSections.initial.fields.length,
      documentType: ''
    }),
}));